import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { LocationService, ZoneService } from "@services";
import utils from "@utils";
import { ConfirmationModal, LocationModal } from "../../../../../components";

import "./ProjectLocations.scss";

const ProjectLocations = ({ project, projectId, user }) => {
	const { lang } = global;
	let _confirmationModal = useRef();
	const [locations, setLocations] = useState([]);
	const [filteredLocations, setFilteredLocations] = useState(undefined);
	const [addingLocation, setAddingLocation] = useState(false);
	const [editingLocation, setEditingLocation] = useState(undefined);
	const [zones, setZones] = useState([]);

	/**
	 * Retrieves a list of locations associated with a project
	 * @param {'projectId'} params The parameter used to retieve a location
	 */
	const getLocations = params => {
		LocationService.getAll(params)
			.then(({ data }) => {
				setLocations(data);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Locations"),
					title: "Error",
				});
			});
	};

	const openDeleteConfirmationModal = locationId => () => {
		const onConfirm = () => {
			LocationService.delete(locationId)
				.then(() => {
					setLocations(prevLocations =>
						prevLocations.filter(location => location.Id !== locationId),
					);
					utils.notify({
						type: "success",
						message: lang.t("Location sucessfully deleted"),
						title: lang.t("Success"),
					});
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error deleting location"),
						title: lang.t("Error"),
					});
				});
		};
		_confirmationModal.openModal({
			title: lang.t("Delete Location"),
			message: lang.t("Are you sure you want to delete this location_"),
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Location")}`,
			onConfirm,
		});
	};

	/** Searches for a locatoin from the list */
	const filterLocations = ({ target: { value: searchQuery } }) => {
		const findInProperty = property =>
			property.toString().toLowerCase().includes(searchQuery.toLowerCase());

		const newLocations = locations.filter(location => {
			let match;

			match = findInProperty(location.Id);
			match = !match ? findInProperty(location.Name) : match;
			match = !match && location.address ? findInProperty(location.StreetAddress1) : match;
			match = !match && location.city ? findInProperty(location.StreetAddress2) : match;
			match = !match && location.city ? findInProperty(location.City) : match;
			match = !match && location.state ? findInProperty(location.State) : match;
			match = !match && location.zipcode ? findInProperty(location.Zipcode) : match;

			return match;
		});

		setFilteredLocations(newLocations);
	};

	const getMappedZones = data => {
		if (data.length !== 0) {
			return data.map(item => ({ value: item.Id, label: item.Name }));
		}
		return [];
	};

	useEffect(() => {
		/*
		location list has no pagination when the projectId param is present,
		it shows all the locations matching the filters at once
		*/
		getLocations({ projectId });
		/*
		load all zones
		*/
		if (user.Organization.UseZones) {
			ZoneService.getAll()
				.then(({ data }) => {
					setZones(data);
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Location Zones"),
						title: "Error",
					});
				});
		}
	}, []);

	return (
		<div
			id="project-locations"
			className="container-fluid"
		>
			<div
				id="top-panel"
				className="row"
			>
				<div className="col-md-4">
					<div className="form-group">
						<label
							htmlFor="searchLocation"
							className="control-label"
						>
							{lang.t("Search Location")}
						</label>
						<input
							id="search-location"
							className="form-control"
							placeholder="Search..."
							onChange={filterLocations}
						/>
					</div>
				</div>
				<div className="col-md-8">
					<button
						id="add-location"
						type="button"
						className="btn btn-primary pull-right"
						onClick={() => setAddingLocation(true)}
					>
						{`+ ${lang.t("New Location")}`}
					</button>
				</div>
			</div>
			<div
				id="table-header"
				className="row hidden-xs"
			>
				<div className="col-sm-1">
					<strong>ID</strong>
				</div>
				<div className="col-sm-2 col-md-3">
					<strong>{lang.t("Name")}</strong>
				</div>
				<div className="col-sm-4 col-md-3">
					<strong>{lang.t("Address")}</strong>
				</div>
				<div className="col-sm-4 col-md-3" />
			</div>
			<div className="row">
				<div
					id="table-body"
					className="container-fluid"
				>
					<For
						each="item"
						of={filteredLocations ?? locations}
					>
						<div
							className="row v-align-sm"
							key={`location_${item.Id}`}
						>
							<div className="col-xs-3 col-sm-1">{item.Id}</div>
							<div
								className="location-title col-xs-9 col-sm-2 col-md-3"
								title={item.Name}
							>
								<span className="hidden-xs">{item.Name}</span>
								<strong className="visible-xs">{item.Name}</strong>
							</div>
							<div className="col-xs-6 col-sm-4 col-md-3 location-address">
								{item.StreetAddress1}
								<If condition={item.StreetAddress2}>
									<br />
									{item.StreetAddress2}
								</If>
								<br />
								{item.City} {item.City && item.State ? ", " : ""} {item.State}{" "}
								{item.Zipcode}
							</div>
							<div className="col-xs-12 col-sm-4 col-md-3 col-sm-offset-1 col-md-offset-2">
								<div className="row">
									<div className="col-xs-6">
										<button
											type="button"
											className="action-btn btn btn-primary btn-block"
											onClick={() => setEditingLocation(item.Id)}
										>
											{lang.t("Edit")}
										</button>
									</div>
									<div className="col-xs-6">
										<button
											type="button"
											className="action-btn btn btn-primary btn-block"
											onClick={openDeleteConfirmationModal(item.Id)}
										>
											{lang.t("Delete")}
										</button>
									</div>
								</div>
							</div>
						</div>
					</For>
				</div>
			</div>
			<LocationModal
				show={addingLocation || editingLocation !== undefined}
				onHide={() => {
					setAddingLocation(false);
					setEditingLocation(undefined);
				}}
				onSuccess={() => {
					setAddingLocation(false);
					setEditingLocation(undefined);
					getLocations({ projectId });
				}}
				zones={getMappedZones(zones)}
				title={`${addingLocation ? lang.t("Add") : lang.t("Edit")} ${lang.t("Location")}`}
				locationId={editingLocation}
				projectId={project.Id}
				user={user}
			/>
			<ConfirmationModal
				ref={ref => {
					_confirmationModal = ref;
				}}
			/>
		</div>
	);
};

ProjectLocations.propTypes = {
	project: PropTypes.object,
	projectId: PropTypes.string.isRequired,
	user: PropTypes.object,
};

ProjectLocations.defaultProps = {
	project: {},
	user: {},
};

export default ProjectLocations;
